import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ProviderNotice from './ProviderNotice';
import { useIdempotencyKey } from './useIdempotencyKey';
import { createNote, searchContacts } from '../../services/CommunicationsService';
import type { InboxMessage, MessageContact, SendMessageInput } from '../../types/Communications';

type Mode = 'guest' | 'phone' | 'note';

interface ComposeModalProps {
  open: boolean;
  onClose: () => void;
  /** WhatsApp is not set up: guest/phone sends are off, notes still work. */
  sendBlocked: boolean;
  onSend: (input: SendMessageInput, idempotencyKey: string) => Promise<InboxMessage>;
  /** Called with the stored message after a send or a saved note. */
  onDone: (message: InboxMessage) => void;
}

const MODES: Array<{ id: Mode; label: string }> = [
  { id: 'guest', label: 'Guest' },
  { id: 'phone', label: 'Phone number' },
  { id: 'note', label: 'Note' },
];

/** "+<country code><number>": strip spacing, require a leading + and 7 to 15 digits. */
export function normalizeInternationalPhone(raw: string): string | null {
  const compact = raw.replace(/[\s().-]/g, '');
  return /^\+[1-9]\d{6,14}$/.test(compact) ? compact : null;
}

const ComposeModal: React.FC<ComposeModalProps> = ({ open, onClose, sendBlocked, onSend, onDone }) => {
  const [mode, setMode] = useState<Mode>('guest');
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState<MessageContact[]>([]);
  const [contactsError, setContactsError] = useState('');
  const [picked, setPicked] = useState<MessageContact | null>(null);
  const [phone, setPhone] = useState('');
  const [text, setText] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const idem = useIdempotencyKey();

  useEffect(() => {
    if (open) {
      setMode('guest'); setSearch(''); setPicked(null); setPhone(''); setText(''); setSubject('');
      setError(''); setContactsError('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'guest') return;
    let cancelled = false;
    const timer = setTimeout(() => {
      searchContacts(search)
        .then(list => { if (!cancelled) { setContacts(list); setContactsError(''); } })
        .catch(err => { if (!cancelled) setContactsError(err instanceof Error ? err.message : 'Could not load guests.'); });
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, mode, search]);

  const sendsMessage = mode !== 'note';
  const disabledByProvider = sendsMessage && sendBlocked;

  const submit = async () => {
    setError('');
    const body = text.trim();
    try {
      if (mode === 'note') {
        if (!subject.trim()) { setError('Add a subject for the note.'); return; }
        setBusy(true);
        onDone(await createNote({ subject: subject.trim(), body }));
        return;
      }
      if (!body) { setError('Write a message first.'); return; }
      let input: SendMessageInput;
      if (mode === 'guest') {
        if (!picked) { setError('Pick a guest to message.'); return; }
        input = { text: body, contactType: 'guest', contactId: picked.id };
      } else {
        const to = normalizeInternationalPhone(phone);
        if (!to) { setError('Enter the number in international form: +<country code><number>.'); return; }
        input = { text: body, to };
      }
      setBusy(true);
      const sent = await onSend(input, idem.keyFor(JSON.stringify(input)));
      idem.reset();
      onDone(sent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New message"
      size="medium"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={disabledByProvider}>
            {mode === 'note' ? 'Save note' : 'Send message'}
          </Button>
        </>
      )}
    >
      <div className="communications-compose">
        <div className="communications-compose-tabs" role="tablist" aria-label="Message type">
          {MODES.map(m => (
            <button key={m.id} type="button" role="tab" aria-selected={mode === m.id}
              className={`communications-compose-tab${mode === m.id ? ' active' : ''}`}
              onClick={() => { setMode(m.id); setError(''); }}>
              {m.label}
            </button>
          ))}
        </div>

        {disabledByProvider && <ProviderNotice />}
        {error && <div className="communications-error" role="alert">{error}</div>}

        {mode === 'guest' && (
          <div className="form-group">
            <label className="form-label" htmlFor="communications-guest-search">Guest</label>
            <input id="communications-guest-search" type="search" className="form-control" value={search}
              onChange={e => setSearch(e.target.value)} placeholder="Search this project's guests…" autoComplete="off" />
            {contactsError && <div className="communications-error" role="alert">{contactsError}</div>}
            <ul className="communications-contact-list" aria-label="Guests">
              {contacts.map(c => (
                <li key={c.id}>
                  <button type="button" disabled={!c.messageable}
                    className={`communications-contact${picked?.id === c.id ? ' communications-contact--picked' : ''}`}
                    aria-pressed={picked?.id === c.id}
                    onClick={() => setPicked(c)}>
                    <span>{c.name}</span>
                    {!c.messageable && <span className="communications-contact-note">No phone number</span>}
                  </button>
                </li>
              ))}
              {contacts.length === 0 && !contactsError && <li className="communications-contact-note">No guests found.</li>}
            </ul>
          </div>
        )}

        {mode === 'phone' && (
          <div className="form-group">
            <label className="form-label" htmlFor="communications-phone">Phone number</label>
            <input id="communications-phone" type="tel" className="form-control" value={phone}
              onChange={e => setPhone(e.target.value)} placeholder="+<country code><number>" autoComplete="off" />
          </div>
        )}

        {mode === 'note' && (
          <div className="form-group">
            <label className="form-label" htmlFor="communications-subject">Subject</label>
            <input id="communications-subject" type="text" className="form-control" value={subject}
              onChange={e => setSubject(e.target.value)} />
            <p className="communications-hint">A note is saved in the inbox. Nothing is sent.</p>
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="communications-text">{mode === 'note' ? 'Note' : 'Message'}</label>
          <textarea id="communications-text" className="form-control" rows={5} value={text}
            onChange={e => setText(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

export default ComposeModal;
